import { type ReactElement, type ReactNode } from 'react';

const INLINE_MARKDOWN = /(`[^`\n]+`|\*\*\*[^*\n]+?\*\*\*|\*\*[^*\n]+?\*\*|\*[^*\n]+?\*|\[[^\]\n]+\]\([^\s)\n]+\))/gu;
const UNORDERED_ITEM = /^\s*[-+*•]\s+(.+)$/u;
const ORDERED_ITEM = /^\s*\d+[.)]\s+(.+)$/u;
const HEADING = /^(#{1,3})\s+(.+)$/u;
const BLOCKQUOTE = /^\s*>\s?(.*)$/u;
const INLINE_BULLET_SEPARATOR = /\s+•\s+|^\s*•\s+/u;

function safeLink(href: string): boolean {
  if (href.startsWith('#')) return true;
  if (href.startsWith('/')) return !href.startsWith('//');
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:';
  } catch {
    return false;
  }
}

function inlineNodes(source: string, keyPrefix: string): readonly ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let sequence = 0;

  for (const match of source.matchAll(INLINE_MARKDOWN)) {
    const index = match.index;
    const token = match[0];
    if (index > cursor) nodes.push(source.slice(cursor, index));
    const key = `${keyPrefix}-${sequence}`;
    sequence += 1;

    if (token.startsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('***')) {
      nodes.push(
        <strong key={key}>
          <em>{inlineNodes(token.slice(3, -3), `${key}-content`)}</em>
        </strong>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key}>{inlineNodes(token.slice(2, -2), `${key}-content`)}</strong>,
      );
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key}>{inlineNodes(token.slice(1, -1), `${key}-content`)}</em>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/u.exec(token);
      if (link === null || !safeLink(link[2]!)) {
        nodes.push(link?.[1] ?? token);
      } else {
        const label = link[1]!;
        const href = link[2]!;
        const external = /^https?:/u.test(href);
        nodes.push(
          <a
            key={key}
            href={href}
            {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
          >
            {inlineNodes(label, `${key}-label`)}
          </a>,
        );
      }
    }
    cursor = index + token.length;
  }

  if (cursor < source.length) nodes.push(source.slice(cursor));
  return nodes;
}

function isBlockStart(line: string): boolean {
  return line.startsWith('```')
    || HEADING.test(line)
    || UNORDERED_ITEM.test(line)
    || ORDERED_ITEM.test(line)
    || BLOCKQUOTE.test(line);
}

function blockLines(source: string): readonly string[] {
  return source.replace(/\r\n?/gu, '\n').split('\n').flatMap((line) => {
    const parts = line.split(INLINE_BULLET_SEPARATOR);
    if (parts.length === 1) return [line];
    const prefix = parts.shift()?.trim() ?? '';
    return [
      ...(prefix === '' ? [] : [prefix]),
      ...parts
        .map((part) => part.trim())
        .filter((part) => part !== '')
        .map((part) => `• ${part}`),
    ];
  });
}

export function AssistantMarkdown({ children }: {
  readonly children: string;
}): ReactElement {
  const lines = blockLines(children);
  const blocks: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index]!;
    if (line.trim() === '') {
      index += 1;
      continue;
    }

    if (line.startsWith('```')) {
      const language = line.slice(3).trim();
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index]!.startsWith('```')) {
        code.push(lines[index]!);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={`code-${index}`}>
          <code {...(language === '' ? {} : { 'data-language': language })}>
            {code.join('\n')}
          </code>
        </pre>,
      );
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading !== null) {
      const content = inlineNodes(heading[2]!, `heading-${index}`);
      const level = heading[1]!.length;
      blocks.push(level === 1
        ? <h2 key={`heading-${index}`}>{content}</h2>
        : level === 2
          ? <h3 key={`heading-${index}`}>{content}</h3>
          : <h4 key={`heading-${index}`}>{content}</h4>);
      index += 1;
      continue;
    }

    const unordered = UNORDERED_ITEM.exec(line);
    if (unordered !== null) {
      const items: ReactNode[] = [];
      while (index < lines.length) {
        const item = UNORDERED_ITEM.exec(lines[index]!);
        if (item === null) break;
        items.push(
          <li key={`unordered-${index}`}>
            {inlineNodes(item[1]!, `unordered-${index}`)}
          </li>,
        );
        index += 1;
      }
      blocks.push(<ul key={`unordered-list-${index}`}>{items}</ul>);
      continue;
    }

    const ordered = ORDERED_ITEM.exec(line);
    if (ordered !== null) {
      const items: ReactNode[] = [];
      while (index < lines.length) {
        const item = ORDERED_ITEM.exec(lines[index]!);
        if (item === null) break;
        items.push(
          <li key={`ordered-${index}`}>
            {inlineNodes(item[1]!, `ordered-${index}`)}
          </li>,
        );
        index += 1;
      }
      blocks.push(<ol key={`ordered-list-${index}`}>{items}</ol>);
      continue;
    }

    const quote = BLOCKQUOTE.exec(line);
    if (quote !== null) {
      const quoted: string[] = [];
      while (index < lines.length) {
        const next = BLOCKQUOTE.exec(lines[index]!);
        if (next === null) break;
        quoted.push(next[1]!);
        index += 1;
      }
      blocks.push(
        <blockquote key={`quote-${index}`}>
          {inlineNodes(quoted.join(' '), `quote-${index}`)}
        </blockquote>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length
      && lines[index]!.trim() !== ''
      && !isBlockStart(lines[index]!)) {
      paragraph.push(lines[index]!.trim());
      index += 1;
    }
    blocks.push(
      <p key={`paragraph-${index}`}>
        {inlineNodes(paragraph.join(' '), `paragraph-${index}`)}
      </p>,
    );
  }

  return (
    <div className="cards-ai-markdown">
      {blocks}
    </div>
  );
}
